import Button from './ui/Button';
import Card from './ui/Card';
import GameModeSelector from './GameModeSelector';
import { OPERATIONS_CONFIG, DIFFICULTY_CONFIG } from '../config/gameConfig';

export default function SetupScreen({
  setupStep,
  playerName,
  selectedOperations,
  difficulty,
  gameMode,
  onNameChange,
  onToggleOperation,
  onDifficultySelect,
  onGameModeSelect,
  onNext,
  onPrev,
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 p-4 flex items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-600 mb-2">
            🎯 Matematikspillet 🎯
          </h1>
          <p className="text-lg text-gray-600">Lad os sætte dit spil op!</p>

          {/* Progress Indicator */}
          <div className="flex justify-center mt-6 space-x-2">
            {[1, 2, 3, 4].map((step, idx) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    setupStep >= step ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  {step}
                </div>
                {idx < 3 && (
                  <div
                    className={`w-12 h-1 ${
                      setupStep >= step + 1 ? 'bg-purple-500' : 'bg-gray-300'
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Name Input */}
        {setupStep === 1 && (
          <div className="text-center">
            <div className="text-6xl mb-6">👋</div>
            <h2 className="text-3xl font-bold text-gray-700 mb-6">
              Hej! Hvad hedder du?
            </h2>
            <input
              type="text"
              value={playerName}
              onChange={(e) => onNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && playerName.trim() !== '') {
                  onNext();
                }
              }}
              className="w-full text-3xl font-bold text-center border-4 border-purple-300 rounded-2xl px-6 py-4 focus:border-purple-500 focus:outline-none mb-8"
              placeholder="Skriv dit navn her..."
              autoFocus
            />
            <Button
              onClick={onNext}
              disabled={playerName.trim() === ''}
              size="lg"
              fullWidth
            >
              Næste! 👉
            </Button>
          </div>
        )}

        {/* Step 2: Operations Selection */}
        {setupStep === 2 && (
          <div className="text-center">
            <div className="text-6xl mb-6">🧮</div>
            <h2 className="text-3xl font-bold text-gray-700 mb-2">
              Hej {playerName}!
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Hvilke regnestykker vil du øve?
            </p>

            <div className="space-y-4 mb-8">
              {Object.values(OPERATIONS_CONFIG).map((op) => (
                <Card
                  key={op.key}
                  onClick={() => onToggleOperation(op.key)}
                  selected={selectedOperations[op.key]}
                  selectable
                >
                  <div className="text-4xl mb-2">{op.icon}</div>
                  <div className="text-xl font-bold">{op.label}</div>
                  <div className="text-gray-600">{op.description}</div>
                </Card>
              ))}
            </div>

            <div className="flex space-x-4">
              <Button onClick={onPrev} variant="secondary" size="lg" className="flex-1">
                👈 Tilbage
              </Button>
              <Button onClick={onNext} size="lg" className="flex-1">
                Næste! 👉
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Difficulty Selection */}
        {setupStep === 3 && (
          <div className="text-center">
            <div className="text-6xl mb-6">⚡</div>
            <h2 className="text-3xl font-bold text-gray-700 mb-2">
              Fantastisk, {playerName}!
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Hvor svært skal det være?
            </p>

            <div className="space-y-4 mb-8">
              {Object.entries(DIFFICULTY_CONFIG).map(([key, config]) => (
                <Card
                  key={key}
                  onClick={() => onDifficultySelect(key)}
                  selected={difficulty === key}
                  selectable
                >
                  <div className="text-4xl mb-2">{config.icon}</div>
                  <div className="text-xl font-bold">{config.label}</div>
                  <div className="text-gray-600">{config.description}</div>
                </Card>
              ))}
            </div>

            <div className="flex space-x-4">
              <Button onClick={onPrev} variant="secondary" size="lg" className="flex-1">
                👈 Tilbage
              </Button>
              <Button onClick={onNext} size="lg" className="flex-1">
                Næste! 👉
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Game Mode Selection */}
        {setupStep === 4 && (
          <div className="text-center">
            <div className="text-6xl mb-6">🎮</div>
            <h2 className="text-3xl font-bold text-gray-700 mb-2">
              Vælg spiltilstand!
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Hvordan vil du spille?
            </p>

            <div className="mb-8">
              <GameModeSelector
                selectedMode={gameMode}
                onModeSelect={onGameModeSelect}
              />
            </div>

            <div className="flex space-x-4">
              <Button onClick={onPrev} variant="secondary" size="lg" className="flex-1">
                👈 Tilbage
              </Button>
              <Button onClick={onNext} size="lg" className="flex-1">
                Start Spillet! 🚀
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
